import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 20000 });

// ---------- Catalog ----------
export const getDefaultLearner   = () => http.get(`/learners/default/me`).then(r => r.data);
export const listLearners        = () => http.get(`/learners`).then(r => r.data);
export const listSubjects        = (grade = null, learnerId = null) => {
  const params = {};
  if (grade !== null) params.grade = grade;
  if (learnerId) params.learner_id = learnerId;
  return http.get(`/subjects`, { params }).then(r => r.data);
};
export const listChapters = (subject, learnerId, grade = null) => {
  const params = { learner_id: learnerId };
  if (grade !== null) params.grade = grade;
  return http.get(`/subjects/${encodeURIComponent(subject)}/chapters`, { params }).then(r => r.data);
};
export const listSubtopics       = (subject, learnerId, grade = null) => {
  const params = { learner_id: learnerId };
  if (grade !== null) params.grade = grade;
  return http.get(`/subjects/${encodeURIComponent(subject)}/subtopics`, { params }).then(r => r.data);
};
export const getSubtopic         = (subject, subTopic, grade = null) => {
  const params = {};
  if (grade !== null) params.grade = grade;
  return http.get(`/subtopics/${encodeURIComponent(subject)}/${encodeURIComponent(subTopic)}`, { params }).then(r => r.data);
};

// ---------- Engine / Session ----------
export const startSession        = (learnerId, grade = null) => {
  const payload = { learner_id: learnerId, surface: "web" };
  if (grade !== null) payload.grade = grade;
  return http.post(`/session/start`, payload).then(r => r.data);
};

export const startSubtopic       = (sessionId, subject, subTopic) =>
  http.post(`/session/${sessionId}/start-subtopic`, { subject, sub_topic: subTopic }).then(r => r.data);

export const sessionNext         = (sessionId) =>
  http.post(`/session/${sessionId}/next`).then(r => r.data);

export const submitAnswer        = (sessionId, recordId, answerGiven, timeTakenSec = 0) =>
  http.post(`/session/${sessionId}/answer`, {
    record_id: recordId, answer_given: answerGiven, time_taken_sec: timeTakenSec,
  }).then(r => r.data);

export const continueAfterStop   = (sessionId) =>
  http.post(`/session/${sessionId}/continue-after-stop`).then(r => r.data);

export const revisitRecap        = (sessionId, subject, subTopic) =>
  http.post(`/session/${sessionId}/revisit-recap`, { subject, sub_topic: subTopic }).then(r => r.data);

export const endSession          = (sessionId) =>
  http.post(`/session/${sessionId}/end`).then(r => r.data);

export const saveAndExitSession  = (sessionId) =>
  http.post(`/session/${sessionId}/save-and-exit`).then(r => r.data);

// ---------- Guided Review ----------
export const grStep1             = (sessionId, recordId) =>
  http.post(`/session/${sessionId}/guided-review/step1`, { record_id: recordId }).then(r => r.data);
export const grStep2             = (sessionId, recordId) =>
  http.post(`/session/${sessionId}/guided-review/step2`, { record_id: recordId }).then(r => r.data);
export const grStep3             = (sessionId, recordId) =>
  http.post(`/session/${sessionId}/guided-review/step3`, { record_id: recordId }).then(r => r.data);
export const grStep3Answer       = (sessionId, recordId, microStepId, answerGiven, attemptNumber) =>
  http.post(`/session/${sessionId}/guided-review/step3/answer`, {
    record_id: recordId, micro_step_id: microStepId, answer_given: answerGiven, attempt_number: attemptNumber,
  }).then(r => r.data);
export const grStep4             = (sessionId, recordId) =>
  http.post(`/session/${sessionId}/guided-review/step4`, { record_id: recordId }).then(r => r.data);

// ---------- Teacher ----------
export const teacherStudents     = (teacherId, grade = null) => {
  const params = {};
  if (grade !== null) params.grade = grade;
  return http.get(`/teacher/${teacherId}/students`, { params }).then(r => r.data);
};
export const teacherNotifications = (teacherId, onlyUnread = false) =>
  http.get(`/teacher/${teacherId}/notifications`, { params: { only_unread: onlyUnread } }).then(r => r.data);
export const markNotifReviewed   = (notifId) =>
  http.post(`/teacher/notifications/${notifId}/review`).then(r => r.data);
export const teacherStudentJourney = (learnerId) =>
  http.get(`/teacher/student/${learnerId}/journey`).then(r => r.data);
export const learnerJourney     = (learnerId) =>
  http.get(`/learner/${learnerId}/journey`).then(r => r.data);
export const teacherStudentStops = (learnerId) =>
  http.get(`/teacher/student/${learnerId}/stops`).then(r => r.data);
export const teacherUnlock       = (teacherId, learnerId, subject, subTopic, unlockType = "practice_retry") =>
  http.post(`/teacher/unlock`, { 
    teacher_id: teacherId, 
    learner_id: learnerId, 
    subject, 
    sub_topic: subTopic,
    unlock_type: unlockType 
  }).then(r => r.data);
// ---------- Teacher (Phase 4) ----------
export const teacherStopDetail = (stopId) =>
  http.get(`/teacher/stop/${stopId}`).then(r => r.data);
export const teacherSubtopicDrilldown = (learnerId, subject, subTopic) =>
  http.get(`/teacher/student/${learnerId}/subtopic/${encodeURIComponent(subject)}/${encodeURIComponent(subTopic)}`).then(r => r.data);
export const teacherColdPool = (teacherId, learnerId) =>
  http.get(`/teacher/${teacherId}/cold-pool/${learnerId}`).then(r => r.data);

// ── Teacher cockpit (Class triage) ────────────────────────────────────────
export const teacherGrades = (teacherId) =>
  http.get(`/teacher/${teacherId}/grades`).then(r => r.data);
export const teacherSubjectsForGrade = (teacherId, grade) =>
  http.get(`/teacher/${teacherId}/subjects-for-grade`, { params: { grade } }).then(r => r.data);
export const teacherCockpit = (teacherId, grade, subject) =>
  http.get(`/teacher/${teacherId}/cockpit`, { params: { grade, subject } }).then(r => r.data);
export const teacherPrefsGet = (teacherId) =>
  http.get(`/teacher/${teacherId}/prefs`).then(r => r.data);
export const teacherPrefsSet = (teacherId, cardMetric) =>
  http.post(`/teacher/${teacherId}/prefs`, { card_metric: cardMetric }).then(r => r.data);
export const teacherStudentSummary = (learnerId) =>
  http.get(`/teacher/student/${learnerId}/summary`).then(r => r.data);


export const teacherStudentList = (teacherId, grade, subject = null) => {
  const params = { grade };
  if (subject) params.subject = subject;
  return http.get(`/teacher/${teacherId}/student-list`, { params }).then(r => r.data);
};

// ── Class Topic Controls ───────────────────────────────────────────────────
export const listChaptersRaw = (subject, grade, teacherId = null) => {
  const params = { grade };
  if (teacherId) params.teacher_id = teacherId;
  return http.get(`/subjects/${encodeURIComponent(subject)}/chapters`, { params }).then(r => r.data);
};

export const getClassTopicControls = (teacherId, grade, subject) =>
  http.get(`/teacher/class-topic-controls`, {
    params: { teacher_id: teacherId, grade, subject },
  }).then(r => r.data);

export const updateClassTopicControls = (teacherId, grade, subject, unlockedTopics = [], studentUnlocks = []) =>
  http.put(`/teacher/class-topic-controls`, {
    teacher_id: teacherId,
    grade,
    subject,
    unlocked_topics: unlockedTopics,
    student_unlocks: studentUnlocks,
  }).then(r => r.data);

export const toggleTopicException = (teacherId, grade, subject, learnerId, topic, action) =>
  http.post(`/teacher/class-topic-controls/exception`, {
    teacher_id: teacherId,
    grade,
    subject,
    learner_id: learnerId,
    topic,
    action,
  }).then(r => r.data);

export const getTopicsEngagement = (teacherId, grade, subject) =>
  http.get(`/teacher/${teacherId}/topics-engagement`, { params: { grade, subject } }).then(r => r.data);

export const getEngagementTracker = (teacherId, grade, range = "today") =>
  http.get(`/teacher/${teacherId}/engagement-tracker`, { params: { grade, range } }).then(r => r.data);

export const teacherHeatmap = (teacherId, subject = null, opts = {}) => {
  const params = {};
  if (subject) params.subject = subject;
  if (opts.attention_threshold !== undefined && opts.attention_threshold !== null && opts.attention_threshold !== "")
    params.attention_threshold = opts.attention_threshold;
  if (opts.attention_pct !== undefined && opts.attention_pct !== null)
    params.attention_pct = opts.attention_pct;
  return http.get(`/teacher/${teacherId}/heatmap`, { params }).then(r => r.data);
};

// ---------- Generation (Phase 6) ----------
export const generationPoolStatus = (subject, subTopic) =>
  http.get(`/generation/pool/${encodeURIComponent(subject)}/${encodeURIComponent(subTopic)}`).then(r => r.data);
export const generationRefill = (subject, subTopic, batch = 5) =>
  http.post(`/generation/refill/${encodeURIComponent(subject)}/${encodeURIComponent(subTopic)}?batch=${batch}`).then(r => r.data);

// ---------- Diagrams (v6.2) ----------
// SVG is cached server-side per record_id — first call generates via Claude, subsequent calls return cache.
export const fetchDiagramSvg  = (recordId, { timeout = 60000 } = {}) =>
  http.post(`/diagram/${encodeURIComponent(recordId)}/svg`, null, { timeout }).then(r => r.data);
// Help text — NOT cached, fresh Claude call every tap.
export const fetchDiagramHelp = (recordId, { timeout = 30000 } = {}) =>
  http.post(`/diagram/${encodeURIComponent(recordId)}/help`, null, { timeout }).then(r => r.data);

// ---------- Tell me more (Mitraa) — never cached ----------
export const tellMeMore = ({ scope, recordId, learnerId, wrongOptionChosen }, { timeout = 30000 } = {}) =>
  http.post(`/tell-me-more`, { scope, record_id: recordId, learner_id: learnerId, wrong_option_chosen: wrongOptionChosen ?? null }, { timeout }).then(r => r.data);

export const flagSimplification = (recordId, wrongOptionChosen, responseText) =>
  http.post(`/tell-me-more/flag`, {
    record_id: recordId,
    wrong_option_chosen: wrongOptionChosen ?? null,
    response_text: responseText ?? null,
  }).then(r => r.data);

// ── Mitraa Responses admin ────────────────────────────────────────────────────
export const getMitraaFlagged = (token) =>
  http.get(`/admin/mitraa-responses/flagged`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const refreshMitraaResponse = (payload, token) =>
  http.post(`/admin/mitraa-responses/refresh`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 60000,
  }).then(r => r.data);

export const editMitraaResponse = (payload, token) =>
  http.post(`/admin/mitraa-responses/edit`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const resolveMitraaResponse = (payload, token) =>
  http.post(`/admin/mitraa-responses/resolve`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);


// ---------- Your Sky ----------
export const getSkyStars = (learnerId) =>
  http.get(`/sky/stars/${encodeURIComponent(learnerId)}`).then(r => r.data);

// ---------- Math Foundations ----------
export const getFmLearnerState = (learnerId) =>
  http.get(`/fm/learner/${encodeURIComponent(learnerId)}/state`).then(r => r.data);

export const startFmSession = (learnerId, skill) =>
  http.post(`/fm/session/start`, { learner_id: learnerId, skill }).then(r => r.data);

export const answerFmQuestion = (sessionId, answerGiven, timeTakenMs = null) =>
  http.post(`/fm/session/${sessionId}/answer`, { answer_given: answerGiven, time_taken_ms: timeTakenMs }).then(r => r.data);

export const unlockFmSkill = (learnerId, skill) =>
  http.post(`/fm/admin/unlock`, null, { params: { learner_id: learnerId, skill } }).then(r => r.data);

export const getTeacherFmPanel = (teacherId, grade) =>
  http.get(`/teacher/${teacherId}/fm-panel`, { params: { grade } }).then(r => r.data);

export const getTeacherFmTimeStats = (teacherId, grade) =>
  http.get(`/teacher/${teacherId}/fm-time-stats`, { params: { grade } }).then(r => r.data);

export const teacherFmUnlock = (teacherId, learnerId, skill) =>
  http.post(`/teacher/${teacherId}/fm-unlock`, { learner_id: learnerId, skill }).then(r => r.data);

// ── Practice Test Generator ──────────────────────────────────────────────
export const testGeneratorMetadata = (subject = null, chapter = null, grade = null) => {
  const params = {};
  if (subject) params.subject = subject;
  if (chapter) params.chapter = chapter;
  if (grade) params.grade = grade;
  return http.get(`/teacher/test-generator/metadata`, { params }).then(r => r.data);
};
export const generateTest = (payload) =>
  http.post(`/teacher/test-generator/generate`, payload, { timeout: 60000 }).then(r => r.data);
export const generateTestAsync = (payload) =>
  http.post(`/teacher/test-generator/generate-async`, payload, { timeout: 30000 }).then(r => r.data);
export const getTestGenerationStatus = (jobId) =>
  http.get(`/teacher/test-generator/generate-status/${jobId}`, { timeout: 10000 }).then(r => r.data);
export const refreshQuestion = (payload) =>
  http.post(`/teacher/test-generator/refresh-question`, payload, { timeout: 30000 }).then(r => r.data);
export const finalizeTest = (payload) =>
  http.post(`/teacher/test-generator/finalize`, payload).then(r => r.data);
export const publishTest = (payload) =>
  http.post(`/teacher/test-generator/publish`, payload).then(r => r.data);

// ── Teacher Test Results ─────────────────────────────────────────────────────
export const listTeacherTestResults = (teacherId) =>
  http.get(`/teacher/test-results`, { params: { teacher_id: teacherId } }).then(r => r.data);
export const listDeletedTests = (teacherId) =>
  http.get(`/teacher/test-results`, { params: { teacher_id: teacherId, status: "deleted" } }).then(r => r.data);
export const getTestResultDetail = (testId) =>
  http.get(`/teacher/test-results/${testId}`).then(r => r.data);
export const awardTitaMarks = (testId, learnerId, marksAwarded) =>
  http.post(`/teacher/test-results/${testId}/student/${learnerId}/award-marks`, { marks_awarded: marksAwarded }).then(r => r.data);
export const publishTestScores = (testId) =>
  http.post(`/teacher/test-results/${testId}/publish-scores`).then(r => r.data);
export const bulkSoftDeleteTests = (testIds) =>
  http.post(`/teacher/test-results/bulk-soft-delete`, { test_ids: testIds }).then(r => r.data);
export const restoreTest = (testId) =>
  http.post(`/teacher/test-results/${testId}/restore`).then(r => r.data);
export const hardDeleteTest = (testId) =>
  http.delete(`/teacher/test-results/${testId}`).then(r => r.data);

// ── FM Test Generation ────────────────────────────────────────────────────────
export const generateFMTest = (payload) =>
  http.post(`/teacher/fm-test/generate`, payload).then(r => r.data);
export const publishFMTest = (payload) =>
  http.post(`/teacher/fm-test/publish`, payload).then(r => r.data);

// ── Student Tests ─────────────────────────────────────────────────────────────
export const getStudentTests = (learnerId) =>
  http.get(`/student/tests/${learnerId}`).then(r => r.data);
export const getStudentTestDetail = (learnerId, testId) =>
  http.get(`/student/tests/${learnerId}/${testId}`).then(r => r.data);
export const submitStudentTest = (testId, learnerId, answers) =>
  http.post(`/student/tests/${testId}/submit`, { learner_id: learnerId, answers }).then(r => r.data);
export const abandonStudentTest = (testId, learnerId, answers) =>
  http.post(`/student/tests/${testId}/abandon`, { learner_id: learnerId, answers }).then(r => r.data);

// ---------- Topic Allocation (admin) ----------
export const getTopicAllocations = (schoolCode, token) =>
  http.get(`/admin/cr/schools/${schoolCode}/topic-allocation`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const setTopicAllocation = (schoolCode, grade, subject, allowedTopics, token) =>
  http.put(`/admin/cr/schools/${schoolCode}/topic-allocation`, {
    grade, subject, allowed_topics: allowedTopics,
  }, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);

export const resetTopicAllocation = (schoolCode, grade, subject, token) =>
  http.delete(`/admin/cr/schools/${schoolCode}/topic-allocation`, {
    params: { grade, subject },
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

// ---------- FLN (Foundational Literacy & Numeracy) ----------
export const getFLNLearnerState = (learnerId) =>
  http.get(`/fln/learner-state/${encodeURIComponent(learnerId)}`).then(r => r.data);

export const initFLNLearnerState = (learnerId, schoolId = "") =>
  http.post(`/fln/learner-state/${encodeURIComponent(learnerId)}/init`, null, {
    params: schoolId ? { school_id: schoolId } : {},
  }).then(r => r.data);

export const getFLNItems = (head, level, type = "practice") =>
  http.get(`/fln/items`, { params: { head, level, type } }).then(r => r.data);

export const updateFLNCursor = (learnerId, head, currentLevel, cursorStatus) =>
  http.patch(`/fln/learner-state/${encodeURIComponent(learnerId)}/cursor`, {
    head, current_level: currentLevel, cursor_status: cursorStatus,
  }).then(r => r.data);

// ---------- FLN Assessment ----------
export const generateFLNAssessment = (payload) =>
  http.post(`/fln/assessment/generate`, payload).then(r => r.data);

export const getActiveFLNAssessment = (learnerId, head) =>
  http.get(`/fln/assessment/active`, { params: { learner_id: learnerId, head } }).then(r => r.data);

export const getFLNAssessmentSession = (sessionId) =>
  http.get(`/fln/assessment/${sessionId}`).then(r => r.data);

export const respondFLNAssessment = (sessionId, itemId, isCorrect) =>
  http.patch(`/fln/assessment/${sessionId}/respond`, { item_id: itemId, is_correct: isCorrect }).then(r => r.data);

export const completeFLNAssessment = (sessionId) =>
  http.patch(`/fln/assessment/${sessionId}/complete`).then(r => r.data);

export const verdictFLNAssessment = (sessionId, verdict) =>
  http.patch(`/fln/assessment/${sessionId}/verdict`, { verdict }).then(r => r.data);

// ── Pre-warm (admin) ──────────────────────────────────────────────────────────
export const poolReadinessCold = (grade, subject, token) => {
  const params = {};
  if (grade) params.grade = grade;
  if (subject) params.subject = subject;
  return http.get(`/admin/cr/pool-readiness/cold`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);
};

export const prewarmStart = (items, token) =>
  http.post(`/admin/cr/prewarm`, { items }, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const prewarmStatus = (jobId, token) =>
  http.get(`/admin/cr/prewarm/status/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const getFLNHealth = (token) =>
  http.get(`/admin/fln/health`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

// ---------- Question Flags (student) ----------
export const flagQuestion = (payload) =>
  http.post(`/questions/flag`, payload).then(r => r.data);

// ---------- Question Flags (admin) ----------
export const getQuestionFlags = (token) =>
  http.get(`/admin/question-flags`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const editQuestion = (recordId, collection, payload, token) =>
  http.patch(`/admin/questions/${recordId}`, payload, {
    params: { collection },
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

export const dismissQuestionFlags = (recordId, token) =>
  http.patch(`/admin/question-flags/dismiss/${recordId}`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.data);

// ---------- Try Mitraa (public demo, no auth) ----------
export const tryListTopics = (grade = null, subject = null) => {
  const params = {};
  if (grade) params.grade = grade;
  if (subject) params.subject = subject;
  return http.get(`/try/topics`, { params }).then(r => r.data);
};

export const tryGetBundle = (grade, subject, subTopic, practiceCount = 4) =>
  http.get(`/try/bundle`, { params: { grade, subject, sub_topic: subTopic, practice_count: practiceCount } }).then(r => r.data);

export const trySubmitAttempt = (recordId, selectedOption) =>
  http.post(`/try/attempt`, { record_id: recordId, selected_option: selectedOption }).then(r => r.data);
